import { inject, Injectable } from '@angular/core';
import { HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { createRequestOption } from 'app/shared/util/request-util';
import { GlobalInsurance, InsuranceItem } from '../models/insurance-item.model';
import { ApiProviderService } from './api-provider.service';
import { API_ENDPOINTS } from '../models/enums/endpoints.enum';
import { IBaryTableFetchService, TableOptions } from './i-bary-table-fetch-service';
import { mapAsBody } from '../app/shared/util/request-util';
import { map } from 'rxjs/operators';

export type EntityResponseType = HttpResponse<InsuranceItem>;
export type EntityArrayResponseType = HttpResponse<InsuranceItem[]>;

@Injectable({ providedIn: 'root' })
export class InsuranceItemService implements IBaryTableFetchService<InsuranceItem> {
  readonly #apiProvider = inject(ApiProviderService);

  protected resourceUrl = API_ENDPOINTS.INSURANCE_ITEMS;
  protected insuranceItemsByOfferIdUrl = API_ENDPOINTS.INSURANCE_ITEMS_OFFER_ID;
  protected insuranceItemsGlobal = API_ENDPOINTS.INSURANCE_ITEMS_GLOBAL;

  public fillDataToShow(entities: InsuranceItem[]): Observable<InsuranceItem[]> {
    return of(entities);
  }

  public findAllByOfferId(id: number): Observable<EntityArrayResponseType> {
    const params = new HttpParams().append('offerId.equals', id);
    return this.#apiProvider.publicGet<InsuranceItem[]>(`${this.insuranceItemsByOfferIdUrl(id)}`, {
      params,
      observe: 'response',
    });
  }

  public query(req?: any, tableOption?: TableOptions): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.#apiProvider
      .get<InsuranceItem[]>(this.insuranceItemsByOfferIdUrl(tableOption?.identifier), {
        params: options,
        observe: 'response',
      })
      .pipe(
        map((res: EntityArrayResponseType) => {
          return new HttpResponse<InsuranceItem[]>({
            body: (res.body || []).map(v => new InsuranceItem(v)),
            status: res.status,
            headers: res.headers,
            url: res.url as string,
          });
        })
      );
  }

  public updateGlobalInsuranceItemConfiguration(globalInsurance: GlobalInsurance): Observable<GlobalInsurance> {
    return this.#apiProvider.put<GlobalInsurance>(this.insuranceItemsGlobal(), globalInsurance).pipe(mapAsBody);
  }

  public getGlobalInsuranceItemConfiguration(): Observable<GlobalInsurance> {
    return this.#apiProvider.get<GlobalInsurance>(this.insuranceItemsGlobal()).pipe(mapAsBody);
  }

  public delete(id: number): Observable<HttpResponse<{}>> {
    return this.#apiProvider.delete(`${this.resourceUrl}/${id}`);
  }

  public update(insuranceItem: InsuranceItem): Observable<InsuranceItem> {
    return this.#apiProvider.put<InsuranceItem>(`${this.resourceUrl}/${insuranceItem.id}`, insuranceItem).pipe(mapAsBody);
  }
}
